import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { CircleAlert, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PillArrow, pillClassName } from '@/components/generals/pill-link'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Newsletter signup for dark surfaces (used in the footer).
export const NewsletterForm: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        if (!EMAIL_PATTERN.test(email.trim())) {
            setStatus('error')
            setMessage("Informe um e-mail válido.")
            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), website: honeypot })
            })

            const data = (await response.json().catch(() => null)) as { ok?: boolean, message?: string } | null

            if (!response.ok || !data?.ok) {
                setStatus('error')
                setMessage(data?.message ?? "Não foi possível concluir sua inscrição agora. Tente novamente.")
                return
            }

            setEmail('')
            setStatus('success')
            setMessage(data.message ?? "Inscrição confirmada. Obrigado!")
        }
        catch {
            setStatus('error')
            setMessage("Não conseguimos conectar ao servidor. Verifique sua conexão e tente novamente.")
        }
    }

    return (
        <form noValidate onSubmit={onSubmit} className='relative'>
            <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-px w-px overflow-hidden'>
                <label htmlFor='newsletter-website'>Deixe este campo vazio</label>
                <input
                    id='newsletter-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={honeypot}
                    onChange={(event) => setHoneypot(event.target.value)}
                />
            </div>

            <label htmlFor='newsletter-email' className='font-mono text-[10px] uppercase tracking-[0.18em] text-white/45'>
                Receba nossa Newsletter exclusiva!
            </label>

            <div className='mt-4 flex max-w-md flex-col gap-3 sm:flex-row'>
                <input
                    id='newsletter-email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    placeholder="Seu e-mail"
                    aria-invalid={status === 'error'}
                    aria-describedby={message ? 'newsletter-message' : undefined}
                    value={email}
                    onChange={(event) => {
                        setEmail(event.target.value)
                        if (status !== 'submitting') setStatus('idle')
                    }}
                    className='h-12 min-w-0 flex-1 rounded-full border border-white/20 bg-white/[0.04] px-5 text-sm text-white outline-none transition-colors placeholder:text-white/40 hover:border-white/40 focus:border-white focus-visible:ring-2 focus-visible:ring-highlight/40 aria-[invalid=true]:border-destructive'
                />

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className={pillClassName('highlight', 'md', 'h-12')}
                >
                    {status === 'submitting' ? (
                        <>
                            Enviando
                            <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                        </>
                    ) : (
                        <>
                            Inscrever
                            <PillArrow />
                        </>
                    )}
                </button>
            </div>

            <p
                id='newsletter-message'
                role='status'
                className={cn(
                    'mt-3 flex min-h-5 items-center gap-1.5 text-sm',
                    status === 'error' ? 'text-red-300' : 'text-white/70'
                )}
            >
                {status === 'success' && <CircleCheck aria-hidden='true' className='size-4 shrink-0 text-highlight' />}
                {status === 'error' && <CircleAlert aria-hidden='true' className='size-4 shrink-0' />}
                {message}
            </p>
        </form>
    )
}
